/**
 * Environment validation, run once at startup.
 *
 * A missing `VITE_FIREBASE_PROJECT_ID` otherwise surfaces much later as an
 * opaque Firebase error somewhere inside a query. Failing here, with a message
 * that names the variable and points at the setup guide, turns a confusing
 * afternoon into a ten-second fix.
 *
 * On the "secrecy" of these values: the six `VITE_FIREBASE_*` variables are
 * public by design. They ship in the JavaScript bundle and anyone can read them
 * from devtools. They are identifiers, not credentials — what protects the data
 * is the security rules, App Check and the blocking function at sign-in. See
 * docs/FIREBASE_SETUP.md.
 */

export interface FirebaseEnv {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  /**
   * The named Firestore database, not `(default)`.
   *
   * Required rather than defaulted on purpose. This project's default database
   * holds an earlier app's data in collections that share these names —
   * `vacationRequests` among them — so a value that silently falls back would
   * point the portal at those records instead of failing. A wrong database is
   * worse than a missing one: it reads, it just reads the wrong thing.
   */
  readonly databaseId: string;
  readonly storageBucket: string;
  readonly messagingSenderId: string;
  readonly appId: string;
  readonly measurementId: string | null;
  readonly appCheckSiteKey: string | null;
  readonly appCheckDebugToken: string | null;
  /** UI hint for the Google chooser. Not the security boundary. */
  readonly corporateDomain: string;
  readonly companyId: string;
  readonly useEmulators: boolean;
}

export class EnvironmentError extends Error {
  constructor(readonly missing: readonly string[]) {
    super(
      `Configuración de Firebase incompleta. Falta: ${missing.join(', ')}. ` +
        'Copia .env.example a .env.local y rellena los valores del proyecto ' +
        '(Consola de Firebase › Configuración del proyecto › Tus apps › SDK). ' +
        'Guía completa en docs/FIREBASE_SETUP.md.',
    );
    this.name = 'EnvironmentError';
  }
}

const REQUIRED = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_DATABASE_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

const optional = (value: string | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed.length === 0 ? null : trimmed;
};

/**
 * Reads and validates the environment. Throws `EnvironmentError` listing every
 * missing variable at once, rather than failing on the first — a fresh checkout
 * is missing all six, and reporting them one per run is needlessly slow.
 */
export const readFirebaseEnv = (env: ImportMetaEnv = import.meta.env): FirebaseEnv => {
  const raw = env as unknown as Record<string, string | undefined>;
  const missing = REQUIRED.filter((key) => (raw[key]?.trim() ?? '').length === 0);
  if (missing.length > 0) throw new EnvironmentError(missing);

  // Trimmed on the way in. A stray space after the `=` in a hand-edited .env is
  // easy to introduce and produces an authentication failure whose message says
  // nothing about whitespace.
  const required = (key: string): string => (raw[key] ?? '').trim();

  return {
    apiKey: required('VITE_FIREBASE_API_KEY'),
    authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required('VITE_FIREBASE_PROJECT_ID'),
    databaseId: required('VITE_FIREBASE_DATABASE_ID'),
    storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: required('VITE_FIREBASE_APP_ID'),
    measurementId: optional(raw['VITE_FIREBASE_MEASUREMENT_ID']),
    appCheckSiteKey: optional(raw['VITE_FIREBASE_APPCHECK_SITE_KEY']),
    appCheckDebugToken: optional(raw['VITE_FIREBASE_APPCHECK_DEBUG_TOKEN']),
    // Normalised so a value written either as "knotion.com" or "@knotion.com"
    // works — the handoff's configuration screen shows it with the leading @.
    corporateDomain: (raw['VITE_CORPORATE_DOMAIN'] ?? 'knotion.com')
      .trim()
      .replace(/^@/, '')
      .toLowerCase(),
    companyId: (raw['VITE_COMPANY_ID'] ?? 'knotion').trim(),
    useEmulators: (raw['VITE_USE_EMULATORS'] ?? '').trim().toLowerCase() === 'true',
  };
};
